import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

import { GridTileImage } from '@frontend/storefront/components/grid/tile';
import Image from '@frontend/storefront/Image';
import { useRouter, useSearchParams } from '@frontend/storefront/navigation';

export function Gallery({
  images,
}: {
  images: { src: string; altText: string }[];
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const imageIndex = searchParams.has('image')
    ? parseInt(searchParams.get('image')!)
    : 0;

  const updateImage = (index: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set('image', index);
    router.replace(`?${params.toString()}`, { scroll: false });
  };

  const nextImageIndex = imageIndex + 1 < images.length ? imageIndex + 1 : 0;
  const previousImageIndex =
    imageIndex === 0 ? images.length - 1 : imageIndex - 1;

  const buttonClassName =
    'flex h-full items-center justify-center px-6 transition-all ease-in-out hover:scale-110 hover:text-np-ink';

  return (
    <form>
      <div className="relative aspect-square w-full overflow-hidden rounded-np-lg bg-np-card">
        {images[imageIndex] && (
          <Image
            className="h-full w-full object-cover"
            fill
            sizes="(min-width: 990px) 50vw, 100vw"
            alt={images[imageIndex]?.altText as string}
            src={images[imageIndex]?.src as string}
            preload
          />
        )}

        {images.length > 1 ? (
          <div className="absolute bottom-6 flex w-full justify-center">
            <div className="mx-auto flex h-11 items-center rounded-full bg-np-bg/85 text-np-ink/70 backdrop-blur-sm">
              <button
                formAction={() => updateImage(previousImageIndex.toString())}
                aria-label="Previous product image"
                className={buttonClassName}
              >
                <ArrowLeftIcon className="h-5" />
              </button>
              <div className="mx-1 h-6 w-px bg-np-ink/30"></div>
              <button
                formAction={() => updateImage(nextImageIndex.toString())}
                aria-label="Next product image"
                className={buttonClassName}
              >
                <ArrowRightIcon className="h-5" />
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {images.length > 1 ? (
        <ul className="mt-4 flex flex-wrap items-center gap-2 overflow-auto py-1">
          {images.map((image, index) => {
            const isActive = index === imageIndex;

            return (
              <li key={image.src} className="h-20 w-20">
                <button
                  formAction={() => updateImage(index.toString())}
                  aria-label="Select product image"
                  className="h-full w-full"
                >
                  <GridTileImage
                    alt={image.altText}
                    src={image.src}
                    width={80}
                    height={80}
                    active={isActive}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </form>
  );
}
